import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TeacherNavbar from '../components/TeacherNavbar';
import TeacherClassInvitations from '../components/TeacherClassInvitations';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  Button,
  Typography,
  IconButton,
  Divider,
  Alert, 
  Snackbar, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField, 
  Box, 
  Card, 
  Chip, 
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LinkIcon from '@mui/icons-material/Link';
import AnimationIcon from '@mui/icons-material/Animation';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  addDoc, 
  onSnapshot
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import TeacherAddLesson from '../components/TeacherAddLesson'; 
import '@react-pdf-viewer/core/lib/styles/index.css';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ImageIcon from '@mui/icons-material/Image';
import TeacherQuizCodesDialog from '../components/TeacherQuizCodesDialog';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TeacherUpcomingMeetingsModal from '../components/TeacherUpcomingMeetingsModal';
import CardActionArea from "@mui/material/CardActionArea";
import Badge from "@mui/material/Badge";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useTranslation } from 'react-i18next';
import { getRoleLabels, getStoredTutorType, setStoredTutorType } from '../utils/roleTerminology';

function TeacherClass() {

  const { t } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const [teacherUnreadChats, setTeacherUnreadChats] = useState(0);
  const [upcomingMeeting, setUpcomingMeeting] = useState(null);
  const [allUpcomingMeetings, setAllUpcomingMeetings] = useState([]);   
  const [isUpcomingModalOpen, setIsUpcomingModalOpen] = useState(false); 
  const [categories, setCategories] = useState([]);
  const [allCategoriesOpen, setAllCategoriesOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false); 
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [quizTeacherCode, setQuizTeacherCode] = useState('');
  const [quizStudentCode, setQuizStudentCode] = useState('');
  const QUIZ_TEACHER_LOGIN_URL =
  process.env.REACT_APP_QUIZ_TEACHER_LOGIN_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001/teacherLogin' 
    : 'https://darssy-quizzes.web.app/teacherLogin');
  const { groupId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const [error, setError] = useState('');
  const [totalQuestions, setTotalQuestions] = useState(0);
  const authInstance = getAuth();
  const [teacherMeta, setTeacherMeta] = useState({ name: '', photoURL: ''});
  const [classMeta, setClassMeta] = useState({ name: '', id: '', studentsCount: null,});


  const formatOpenFor = (createdAtMs) => {
    const diffMs = Date.now() - createdAtMs;
    const mins = Math.floor(diffMs / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Turn "YYYY/MM/DD" + "HH:MM" into a JS timestamp (ms)
  const toStartMillis = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split('/').map(Number);   // e.g. 2025/11/03
    const [hh, mm] = timeStr.split(':').map(Number);    // e.g. 15:30
    return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
  };

  // "Starts in" formatter
  const formatStartsIn = (targetMs) => {
    if (!targetMs) return '';
    const diffMs = targetMs - Date.now();
    if (diffMs <= 0) return t('Now');

    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    let result = '';
    if (days > 0) result += `${days}d `;
    if (hours > 0 || days > 0) result += `${hours}h `;
    result += `${minutes}m`;

    return result.trim();
  };


  const fetchUpcomingMeeting = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const teacherUid = user.uid;

      // New structure: meetings/<teacherUid>/<groupId>/<autoDocId>
      const mtgRef = collection(db, 'meetings', teacherUid, groupId);
      const snap = await getDocs(mtgRef);

      const items = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const startMs = toStartMillis(data?.date, data?.startTime);
        items.push({ id: docSnap.id, ...data, startMs });
      });

      const now = Date.now();
      const futureMeetings = items
        .filter((m) => m.startMs && m.startMs >= now)
        .sort((a, b) => a.startMs - b.startMs);

      setAllUpcomingMeetings(futureMeetings);          // NEW: store all future meetings
      const upcoming = futureMeetings[0] || null;      // first one is the next meeting
      setUpcomingMeeting(upcoming);
    } catch (e) {
      console.error('Failed to fetch upcoming meeting:', e);
    }
  }, [auth, groupId]);


  useEffect(() => {
    const fetchTotalQuestions = async () => {
      if (role.isCoach) {
        setTotalQuestions(0);
        return;
      }

      try {
        const groupRef = doc(db, 'groups', groupId);
        const forumRef = collection(groupRef, 'forum');
        const forumSnapshot = await getDocs(forumRef);

        setTotalQuestions(forumSnapshot.size); 
      } catch (error) {
        console.error('Error fetching total questions:', error);
      }
    };

    fetchTotalQuestions();
  }, [groupId, role.isCoach]);


  // Chat unread count (Teacher): sum of conversations.unreadCountTeacher for this teacher UID
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const qConvos = query(
      collection(db, "conversations"),
      where("teacherId", "==", user.uid)
    );

    const unsub = onSnapshot(
      qConvos,
      (snap) => {
        let total = 0;
        snap.forEach((d) => {
          const data = d.data() || {};
          total += Number(data.unreadCountTeacher || 0);
        });
        setTeacherUnreadChats(total);
      },
      (err) => {
        console.error("Teacher unread chat listener error:", err);
        setTeacherUnreadChats(0);
      }
    );

    return () => unsub();
  }, [auth]);


  const openMeetingLink = (url) => {
    if (!url) return;
    // Ensure the link starts with http:// or https://
    const fullUrl = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `https://${url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  
  const redirectToQuizApp = (teacherCode) => {
    if (!teacherCode) return;

    const url = `${QUIZ_TEACHER_LOGIN_URL}?code=${encodeURIComponent(
      teacherCode
    )}&groupId=${encodeURIComponent(groupId)}`;

    // Full redirect to Darssy Quizzes app
    window.location.href = url;
  };

  const handleQuizButtonClick = async () => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);

      if (!groupSnap.exists()) {
        // No group doc yet → ask for codes
        setQuizTeacherCode('');
        setQuizStudentCode('');
        setQuizDialogOpen(true);
        return;
      }

      const data = groupSnap.data();
      const existingTeacherCode = data?.TeacherQuizCode;
      const existingStudentCode = data?.StudentQuizCode;

      // If one or both codes are missing → open dialog prefilled if available
      if (!existingTeacherCode || !existingStudentCode) {
        setQuizTeacherCode(existingTeacherCode || '');
        setQuizStudentCode(existingStudentCode || '');
        setQuizDialogOpen(true);
        return;
      }

      // Both codes exist → go straight to quizzes app
      redirectToQuizApp(existingTeacherCode);
    } catch (err) {
      console.error('Error checking quiz codes:', err);
      alert(t('حدث خطأ أثناء التحقق من بيانات الإستجوابات. حاول مرة أخرى.'));
    }
  };


  const handleFileClick = (file) => {
       if (!file.url) {
         console.error('File URL is not valid');
         return;
       }
       navigate(`/teacher/class/${groupId}/lesson`, {
         state: { fileUrl: file.url, fileType: file.type }
       });
   };
  


  const fetchFiles = useCallback(async () => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const filesSnapshot = await getDocs(collection(groupRef, 'files'));
  
      const fetchedFiles = [];
      filesSnapshot.forEach((doc) => {
        const fileData = { id: doc.id, ...doc.data() };
  
        // Assign default type if it doesn't exist
        if (!fileData.type) {
          fileData.type = 'exercises';
        }
  
        fetchedFiles.push(fileData);
      });
  
      fetchedFiles.sort((a, b) => b.uploadedAt - a.uploadedAt);
  
      const grouped = fetchedFiles.reduce((acc, file) => {
        const category = file.category || t('Uncategorized');
        if (!acc[category]) acc[category] = [];
        acc[category].push(file);
        return acc;
      }, {});
  
      setCategories(Object.entries(grouped));
      // initially: all folders closed
      setOpenCategories(
        Object.fromEntries(Object.keys(grouped).map((key) => [key, false]))
      );
      setAllCategoriesOpen(false);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError(t('حدث خطأ أثناء جلب الملفات'));
    }
  }, [groupId, t]);
  


  const fetchHeaderMeta = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // 1) Teacher meta (teachers collection)
      const teacherQuery = query(
        collection(db, 'teachers'),
        where('uid', '==', user.uid)
      );
      const teacherSnap = await getDocs(teacherQuery);

      if (!teacherSnap.empty) {
        const teacherData = teacherSnap.docs[0].data();
        setStoredTutorType(teacherData?.tutorType || "Teacher");

        // Adjust these field names to match your DB if needed
        const name =
          teacherData?.fullName ||
          teacherData?.name ||
          teacherData?.displayName ||
          user.displayName ||
          t('Teacher');

        const photoURL =
          teacherData?.photoURL ||
          teacherData?.imageUrl ||
          user.photoURL ||
          '';

        setTeacherMeta({ name, photoURL });
      } else {
        setTeacherMeta({
          name: user.displayName || t('Teacher'),
          photoURL: user.photoURL || '',
        });
      }

      // 2) Class meta (groups collection)
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);

      if (groupSnap.exists()) {
        const g = groupSnap.data();

        // Adjust field name if your group name is stored differently
        const className = g?.name || g?.groupName || g?.title || role.classSingular;

        // Optional: if you store student count on group doc
        const studentsCount =
          typeof g?.studentsCount === 'number' ? g.studentsCount : null;

        setClassMeta({
          name: className,
          id: groupId,
          studentsCount,
        });
      } else {
        setClassMeta({ name: role.classSingular, id: groupId, studentsCount: null });
      }
    } catch (e) {
      console.error('Failed to fetch header meta:', e);
      setClassMeta({ name: role.classSingular, id: groupId, studentsCount: null });
    }
  }, [auth, groupId, role.classSingular, t]);


  const verifyPermissions = useCallback(async () => {
    const user = auth.currentUser;

    if (!user) {
      navigate('/teacher/login');
      return false;
    }

    const teacherQuery = query(
      collection(db, 'teachers'),
      where('uid', '==', user.uid)
    );
    const teacherDocs = await getDocs(teacherQuery);

    if (
      teacherDocs.empty ||
      !teacherDocs.docs[0].data().groupIds.includes(groupId)
    ) {
      navigate('/teacher/classes');
      return false;
    }
    return true;
  }, [groupId, navigate, auth]);



  // Meeting dialog state
  const [isMeetingOpen, setIsMeetingOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    duration: '',
    url: '',
  });
  const [savingMeeting, setSavingMeeting] = useState(false);
  const [meetingMsg, setMeetingMsg] = useState({ type: '', text: '' });

  // Helpers: format date and compute duration
  const formatDateYMDSlash = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
  };

  const computeDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const diff = endMin - startMin;
    return diff > 0 ? String(diff) : '';
  };


  const deleteFile = async (fileId, fileName, fileType) => {
    if (!window.confirm(t('Are you sure you want to delete this file?'))) return;

    try {
      // Skip deleting from Storage if it's a link
      if (fileType !== 'link') {
        const storageRef = ref(storage, `groups/${groupId}/${fileName}`);
        await deleteObject(storageRef);
      }

      const groupRef = doc(db, 'groups', groupId);
      await deleteDoc(doc(collection(groupRef, 'files'), fileId));

        fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  }; 
  

  const toggleImportant = async (fileId, currentState) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const fileRef = doc(collection(groupRef, 'files'), fileId);

      await setDoc(fileRef, { markedImportant: !currentState }, { merge: true });
      fetchFiles();
    } catch (err) {
      console.error('Error toggling important:', err);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const hasPermission = await verifyPermissions();
      if (hasPermission) {
        fetchHeaderMeta();
        fetchFiles();
        fetchUpcomingMeeting();
      }
    };
    initialize();
  }, [verifyPermissions, fetchHeaderMeta, fetchFiles, fetchUpcomingMeeting]);

  const handleToggleAllCategories = () => {
    setAllCategoriesOpen((prev) => {
      const next = !prev; // true = open all, false = close all
      setOpenCategories((prevOpen) =>
        Object.fromEntries(Object.keys(prevOpen).map((key) => [key, next]))
      );
      return next;
    });
  };

  const filterFilesByType = (files) =>
    files.filter(
      (file) =>
        file.type === 'file' ||
        file.type === 'video' ||
        file.type === 'audio' ||
        file.type === 'image' ||
        file.type === 'swf' ||
        file.type === 'link'
    );


  const handleDeleteMeeting = async (mtg) => {
    try {
      const user = authInstance.currentUser;
      if (!user || !mtg?.id) return;
      const teacherUid = user.uid;

      await deleteDoc(
        doc(collection(db, 'meetings', teacherUid, groupId), mtg.id)
      );

      // Remove from UI
      setAllUpcomingMeetings((prev) => prev.filter((m) => m.id !== mtg.id));

      // If the main "upcomingMeeting" is the one deleted, refresh / clear it
      setUpcomingMeeting((prev) => (prev?.id === mtg.id ? null : prev));
    } catch (err) {
      console.error('Error deleting meeting:', err);
    }
  };


  const handleCategoryClick = (category) => {
    setOpenCategories((prev) => {
      const isOpen = !!prev[category];

      // If it's already open, close it (all closed)
      if (isOpen) {
        const newState = { ...prev, [category]: false };
        setAllCategoriesOpen(false);
        return newState;
      }

      // Open ONLY this category, close others
      const newState = Object.fromEntries(
        Object.keys(prev).map((key) => [key, key === category])
      );
      setAllCategoriesOpen(false); // we are no longer in "open all" mode
      return newState;
    });
  };


  const ActionTicket = ({ icon, title, subtitle, onClick, accent = "#4f46e5" }) => {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2.25,
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
          overflow: "hidden",
          height: "100%",
          transition: "transform 140ms ease, box-shadow 140ms ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 24px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <CardActionArea onClick={onClick} sx={{ height: "100%", p: 2.1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
            <Avatar
              sx={{
                bgcolor: `${accent}14`,
                color: accent,
                width: 56,
                height: 56,
                fontSize: 26,
              }}
            >
              {icon}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: 16 }}>
                {title}
              </Typography>
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 13.5,
                  mt: 0.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={subtitle}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>
        </CardActionArea>
      </Card>
    );
  };


  return (

    <div
      style={{
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <TeacherNavbar />

      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, md: 4 },
          maxWidth: 1280,
          mx: 'auto',
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        {/* Top Action Tickets */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "100%" }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 22, color: "#0f172a" }}>
              {classMeta.name || role.classSingular}
            </Typography>
            <Typography sx={{ color: "#64748b", mt: 0.4, fontSize: 14 }}>
              {role.isCoach ? t("Manage your program using the quick actions below") : t("Manage your class using the quick actions below")}
            </Typography>
          </Box>

          <TeacherClassInvitations groupId={groupId} groupName={classMeta.name || role.classSingular} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={role.isCoach ? 6 : 3}>
              <ActionTicket
                icon={<LiveTvIcon />}
                title={t("Schedule a meeting")}
                subtitle={role.isCoach ? t("Create and publish a live session for clients") : t("Create and publish a live session for students")}
                accent="#c62f36"
                onClick={() => setIsMeetingOpen(true)}
              />
            </Grid>

            {!role.isCoach && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionTicket
                  icon={<QuestionAnswerIcon />}
                  title={t("Forum")}
                  subtitle={t("Answer student questions ({{count}})", { count: totalQuestions })}
                  accent="#12344d"
                  onClick={() => navigate(`/teacher/class/${groupId}/forum`)}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={role.isCoach ? 6 : 3}>
              <ActionTicket
                icon={
                  <Badge
                    color="error"
                    badgeContent={teacherUnreadChats}
                    invisible={teacherUnreadChats <= 0}
                    overlap="circular"
                  >
                    <ChatIcon />
                  </Badge>
                }
                title={t("Chat")}
                subtitle={role.isCoach ? t("Send and receive messages with clients") : t("Send and receive messages with students")}
                accent="#4f46e5"
                onClick={() => navigate("/teacher/chat")}
              />
            </Grid>

            {!role.isCoach && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionTicket
                  icon={<PsychologyAltIcon />}
                  title={t("Quiz")}
                  subtitle={t("Assess students and view performance analytics")}
                  accent="#10b981"
                  onClick={handleQuizButtonClick}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={role.isCoach ? 6 : 3}>
              <ActionTicket
                icon={<SmartToyIcon />}
                title="Darssy AI"
                subtitle={t('Manage AI-assisted exercises')}
                accent="#7c3aed"
                onClick={() => navigate(`/teacher/class/${groupId}/exercises`)}
              />
            </Grid>
          </Grid>
        </Box>
        </Box>



      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          width: '100%',
          flexDirection: 'row-reverse',
          flexWrap: 'wrap',
        }}
      >

        <style>
          {`
            @media (max-width: 768px) {
              div[style*="flex-direction: row-reverse"] {
                flex-direction: column !important; /* Stack sections vertically */
              }

              div[style*="width: 17%"] {
                width: 100% !important; /* Make FORUM BUTTON full width */
              }

              div[style*="width: 67%"] {
                width: 100% !important; /* Make CLASS CONTENT full width */
              }
            }
          `}
        </style>
        

        {/* CLASS CONTENT Section */}
        <div
          style={{
            backgroundColor: "#ffffff",
            width: "95%",
            padding: "20px",
            marginTop: "20px",
            marginRight: "10px",
            marginBottom: "20px",
            border: "1px solid #eef1f6",
            borderRadius: 18,
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
            {/* Upcoming Meeting Ticket */}
          {upcomingMeeting ? (
            <div style={{ width: '100%', margin: '0 auto 16px auto' }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #eef1f6',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                  overflow: 'hidden',
                  direction: 'ltr',
                  position: 'relative',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                  {/* Left ribbon/time column */}
                  <Box
                    sx={{
                      width: { xs: 140, sm: 170 },
                      backgroundColor: '#f8fafc',
                      borderRight: '1px solid #eef1f6',
                      px: 2,
                      py: 2,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: '#0f172a' }} />

                    <Typography variant="caption" sx={{ color: '#64748b', ml: 1 }}>
                      #{upcomingMeeting.id?.slice(0, 6) || t('TICKET')}
                    </Typography>

                    <Box sx={{ mt: 1.5 }}>
                      <Chip
                        label={t("Upcoming meeting")}
                        size="small"
                        sx={{ borderRadius: '999px', bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700 }}
                      />
                    </Box>

                    <Typography variant="h4" sx={{ mt: 1, fontWeight: 700, color: '#0f172a' }}>
                      {upcomingMeeting.startTime || '--:--'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {upcomingMeeting.date || ''}
                    </Typography>

                    <Box
                      sx={{
                        mt: 2,
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        px: 1.2,
                        py: 0.8,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {t("Starts in:")}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {formatStartsIn(upcomingMeeting.startMs)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Main content */}
                  <Box sx={{ flex: 1, p: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 0.5, // reduced from 1
                        mb: 1, // added small margin bottom
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 200 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#0f172a' }}>
                          {upcomingMeeting.title || t('Untitled meeting')}
                        </Typography>
                        <Typography sx={{ color: '#94a3b8' }}>•</Typography>
                        <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.2 }}>
                          {t('At')}{' '}
                          {(() => {
                            try {
                              return new URL(upcomingMeeting.url).hostname || t('Online');
                            } catch {
                              return t('Online');
                            }
                          })()}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => setIsUpcomingModalOpen(true)}
                          aria-label={t("View all upcoming meetings")}
                        >
                          <CalendarMonthIcon fontSize="medium" sx={{ color: '#b91c1c' }} />
                        </IconButton>

                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openMeetingLink(upcomingMeeting?.url)}
                          sx={{
                            py: 0.3,
                            px: 1.5,
                            fontSize: '0.85rem',
                            borderColor: '#0f172a',
                            color: '#0f172a',
                            '&:hover': { borderColor: '#0f172a', bgcolor: '#eef2f7' },
                          }}
                        >
                          {t("Join")}
                        </Button>
                      </Box>
                    </Box>

                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.2 }}>
                          {t("Date")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.3 }}>
                          {upcomingMeeting.date}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.2 }}>
                          {t("Start Time")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.3 }}>
                          {upcomingMeeting.startTime}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.2 }}>
                          {t("End Time")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.3 }}>
                          {upcomingMeeting.endTime || t('--:--')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.2 }}>
                          {t("Duration")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.3 }}>
                          {upcomingMeeting.duration ? t('{{count}} min', { count: upcomingMeeting.duration }) : t('—')}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.3 }}>
                          {t("Description")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', mb: 0.5, lineHeight: 1.4 }}>
                          {upcomingMeeting.description || t('—')}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: '#475569', mb: 0.3 }}>
                          {t("Meeting URL")}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#1d4ed8',
                            wordBreak: 'break-all',
                            cursor: 'pointer',
                            lineHeight: 1.4,
                          }}
                          onClick={() => openMeetingLink(upcomingMeeting?.url)}
                          title={upcomingMeeting.url}
                        >
                          {upcomingMeeting.url || t('—')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Card>
            </div>
          ) : (
            <div style={{ width: '100%', margin: '0 auto 16px auto' }}>

            </div>
          )}


          <div style={{ margin: '20px auto', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 28, color: '#0f172a', fontWeight: 700 }}>
                {t("Program")}
              </Typography>
            </Box>
          </div>


          <div
            style={{
              display: 'flex',
              flexDirection: 'row-reverse',
              direction: 'rtl',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
              padding: 0,
              margin: 0,
            }}
          >
            <div
              style={{
                width: '95%',
                margin: '0 auto',
                transition: 'width 0.3s ease',
                padding: '10px',
              }}
            > 
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsDialogOpen(true)}
                sx={{
                  fontSize: '14px',
                  direction: 'ltr',
                  bgcolor: '#0f172a',
                  '&:hover': { bgcolor: '#1f2937' },
                }}
              >
                <AddIcon />
                {role.isCoach ? t('إضافة ملفات البرنامج') : t('إضافة ملفات الدروس')}
              </Button>

              {/* Conteneur pour les Boutons de Droite */}
              <div style={{ display: 'flex', gap: '10px' }}>

                {/* Bouton "فتح الكل إغلاق الكل" */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleToggleAllCategories}
                  sx={{
                    fontSize: '14px',
                    color: '#fff',
                    backgroundColor: '#0f172a',
                    border: '1px solid #0f172a',
                    '&:hover': { backgroundColor: '#1f2937' },
                }}
              >
                {allCategoriesOpen ? t('إغلاق الكل') : t('فتح الكل')}
              </Button>
            </div>
          </div>


            {/* Files Section */}
            {categories.length === 0 ? (
              <Typography
                variant="h6"
                style={{
                  textAlign: 'center',
                  direction: 'rtl',
                  marginTop: '20px',
                  color: '#64748b',
                  marginTop: '50px'
                }}
              >
                {t('لم يتم تحميل أي ملفات حتى الآن')}
              </Typography>
            ) : (
              categories.map(([category, files]) => (
              <>
                <div
                  key={category}
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #eef1f6',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    direction: 'rtl',
                    boxShadow: '0 8px 20px rgba(99, 100, 103, 0.05)',
                  }}
                >
                  <Typography
                    variant="h6"
                    onClick={() => handleCategoryClick(category)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      borderRadius: '8px 8px 0 0',
                      textAlign: 'right',
                      fontWeight: 700,
                      color: '#0f172a',
                    }}
                  >
                    <ArrowCircleLeftIcon
                      style={{
                        fontSize: '40px',
                        marginLeft: '7px',
                        transform: openCategories[category] ? 'rotate(-90deg)' : 'rotate(0)',
                        transition: 'transform 0.2s',
                        color: '#0f172a',
                      }}
                    />

                    <b> {category} </b>
                  </Typography>

                  {openCategories[category] && (
                    <div
                      style={{
                        padding: '10px',
                        backgroundColor: '#fff',
                        borderRadius: '0 0 8px 8px',
                      }}
                    >
                      {filterFilesByType(files).map((file) => (
                        <div
                          key={file.id}
                          style={{
                            margin: '10px 0',
                            padding: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            direction: 'rtl',
                            borderRadius: '10px',
                            backgroundColor: '#f8fafc',
                            border: '1px solid #eef1f6',
                          }}
                        >
                          {file.type === 'file' ? (
                            <FilePresentIcon sx={{ fontSize: '30px' }} />
                          ) : file.type === 'video' ? (
                            <OndemandVideoIcon sx={{ fontSize: '30px' }} />
                          ) : file.type === 'audio' ? (
                            <AudiotrackIcon sx={{ fontSize: '30px' }} />
                          ) : file.type === 'image' ? (
                            <ImageIcon sx={{ fontSize: '30px' }} />
                          ) : file.type === 'link' ? (
                            <LinkIcon sx={{ fontSize: '30px'}} />
                          ) : file.type === 'swf' ? (
                            <AnimationIcon sx={{ fontSize: '30px' }} />
                          ) : null}
                          <Typography
                            variant="body1"
                            style={{
                              fontSize: '16px',
                              cursor: 'pointer',
                              flexGrow: 1,
                              marginRight: '10px',
                              color: '#1d4ed8',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => {
                              if (file.type === 'link') {
                                window.open(
                                  file.url.startsWith('http://') || file.url.startsWith('https://')
                                    ? file.url
                                    : `https://${file.url}`,
                                  '_blank'
                                );
                              } else {
                                handleFileClick(file);
                              }
                            }}
                            title={file.originalName || file.name}
                          >
                            {file.originalName || file.name}
                            <Divider sx={{ marginTop: '7px' }} />
                          </Typography>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={() => toggleImportant(file.id, file.markedImportant)}>
                              {file.markedImportant ? (
                                <StarIcon color="warning" />
                              ) : (
                                <StarBorderIcon />
                              )}
                            </IconButton>

                            <IconButton>
                              <DeleteIcon
                                color="error"
                                onClick={() => deleteFile(file.id, file.name, file.type)}
                              />                            
                            </IconButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </>
               )))}
              </div>
            </div>
          </div>
      </div>
      </Box>

      {isDialogOpen && (
        <TeacherAddLesson
          groupId={groupId}
          categories={categories}
          fetchFiles={fetchFiles}
          onClose={() => setIsDialogOpen(false)}
        />
      )}

      {/* Add Meeting Dialog */}
      <Dialog
        open={isMeetingOpen}
        onClose={() => setIsMeetingOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
          {t('إضافة حصة مباشرة')}
        </DialogTitle>
        <DialogContent
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
            "& .MuiInputBase-root": { borderRadius: 2 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, direction: 'rtl' }}>
            <TextField
              label={t('العنوان')}
              value={meetingForm.title}
              onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('الوصف')}
              value={meetingForm.description}
              onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label={t('التاريخ')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={meetingForm.date}
              onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('نهاية الحصة')}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={meetingForm.endTime}
                onChange={(e) => {
                  const endTime = e.target.value;
                  const duration = computeDuration(meetingForm.startTime, endTime);
                  setMeetingForm({ ...meetingForm, endTime, duration });
                }}
                fullWidth
              />
              <TextField
                label={t('بداية الحصة')}
                type="time"
                InputLabelProps={{ shrink: true }}
                value={meetingForm.startTime}
                onChange={(e) => {
                  const startTime = e.target.value;
                  const duration = computeDuration(startTime, meetingForm.endTime);
                  setMeetingForm({ ...meetingForm, startTime, duration });
                }}
                fullWidth
              />
            </Box>
            <TextField
              label={t('المدّة (بالدقائق)')}
              value={meetingForm.duration}
              onChange={(e) => setMeetingForm({ ...meetingForm, duration: e.target.value })}
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            <TextField
              label={t('رابط الحصة (URL)')}
              value={meetingForm.url}
              onChange={(e) => setMeetingForm({ ...meetingForm, url: e.target.value })}
              fullWidth
            />
            {meetingMsg.text && (
              <Alert severity={meetingMsg.type === 'error' ? 'error' : 'success'} sx={{ mt: 1 }}>
                {meetingMsg.text}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={savingMeeting}
            onClick={async () => {
              try {
                setSavingMeeting(true);
                const user = auth.currentUser;
                if (!user) throw new Error(t('لم يتم العثور على حساب المعلم'));
                const teacherUid = user.uid;

                const formattedDate = formatDateYMDSlash(meetingForm.date);
                const payload = {
                  title: meetingForm.title,
                  description: meetingForm.description,
                  date: formattedDate,
                  startTime: meetingForm.startTime,
                  endTime: meetingForm.endTime,
                  duration: meetingForm.duration,
                  url: meetingForm.url,
                  createdAt: new Date(),
                };

                // New structure: meetings/<teacherUid>/<groupId>/<autoDocId>
                await addDoc(collection(db, 'meetings', teacherUid, groupId), payload);

                // refresh upcoming
                fetchUpcomingMeeting();

                setMeetingMsg({ type: 'success', text: t('تم حفظ معلومات الحصة بنجاح!') });
                setMeetingForm({
                  title: '',
                  description: '',
                  date: '',
                  startTime: '',
                  endTime: '',
                  duration: '',
                  url: '',
                });
              } catch (err) {
                console.error(err);
                setMeetingMsg({ type: 'error', text: t('حدث خطأ أثناء حفظ الحصة.') });
              } finally {
                setSavingMeeting(false);
              }
            }}
          >
            {t('حفظ')}
          </Button>
        </DialogActions>
      </Dialog>

      <TeacherQuizCodesDialog
        open={quizDialogOpen}
        onClose={() => setQuizDialogOpen(false)}
        teacherCode={quizTeacherCode}
        studentCode={quizStudentCode}
        onChangeTeacherCode={setQuizTeacherCode}
        onChangeStudentCode={setQuizStudentCode}
        groupId={groupId}
        onSaved={(savedTeacherCode) => {
          setQuizDialogOpen(false);
          redirectToQuizApp(savedTeacherCode);
        }}
      />

      <TeacherUpcomingMeetingsModal
        open={isUpcomingModalOpen}
        onClose={() => setIsUpcomingModalOpen(false)}
        meetings={allUpcomingMeetings}
        onDeleteMeeting={handleDeleteMeeting}
      />

    </div>
  );
}

export default TeacherClass;
